'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Cross2Icon } from '@radix-ui/react-icons'
import * as Sentry from '@sentry/nextjs'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/utils/api-error-handler'

type NewsSlide = {
  id: number
  tag: string
  title: string
  description: string
  image: string
  keywordBadges?: readonly { label: string; icon: React.ComponentType<{ className?: string }> }[]
  keyFeatures?: string[]
}

const NEWS_SLIDES: NewsSlide[] = [
  {
    id: 0,
    tag: 'NEW',
    title: 'ESMFold2',
    description:
      'Fold proteins, DNA, RNA, and ligands with Biohub ESMFold2 — including covalent modifications — in a single FastFold job.',
    image: '/news/Esmfold2-demo.png',
    keyFeatures: [
      'Protein, DNA, RNA, and ligand (CCD / SMILES) inputs',
      'Covalent modifications support',
      'Multi-chain complex folding',
      'Skills support to use with Agents',
    ],
  },
  {
    id: 1,
    tag: 'NEW',
    title: 'Nesso-1',
    description:
      'Co-fold protein–ligand complexes with Nesso-1 and estimate binder affinity in one pass.',
    image: '/news/nesso-promo.png',
    keyFeatures: [
      'Protein + ligand (SMILES or CCD) co-folding',
      'Binder affinity estimation',
      'Designed for protein–ligand pairs',
      'Use with webhooks alongside Fold models',
      'Skills support to use with Agents',
    ],
  },
  {
    id: 2,
    tag: 'NEW',
    title: 'OpenMM Calvados',
    description:
      'Run CALVADOS coarse-grained OpenMM simulations with Folding models and IDR presets, and use with Agent skills.',
    image: '/news/md.gif',
    keyFeatures: [
      'CALVADOS + OpenMM workflow integration',
      'Supports folded proteins and IDR scenarios',
      'Simulation metrics and plot outputs',
      'Use with Agent skills for automation',
    ],
  },
  {
    id: 3,
    tag: 'NEW',
    title: 'OpenFold 3',
    description:
      'Predict biomolecular structures with state-of-the-art accuracy. OpenFold 3 supports proteins, nucleic acids, small molecules, and covalent modifications in a single unified model.',
    image: '/news/openfold-3-promo-2.png',
    keyFeatures: [
      'Protein, DNA, RNA, and ligand structure prediction',
      'Covalent modifications and multi-chain complexes',
    ],
  },
]

type NewsCarouselModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function restoreBodyPointerEvents() {
  // Radix/DismissableLayer can leave pointer-events:none on <body> after a
  // controlled dialog closes, which freezes all clicks until a full refresh.
  document.body.style.removeProperty('pointer-events')
}

export function NewsCarouselModal({ open, onOpenChange }: NewsCarouselModalProps) {
  const [index, setIndex] = React.useState(0)
  const [loadedImages, setLoadedImages] = React.useState<Record<string, boolean>>({})
  const slide = NEWS_SLIDES[index]
  const isFirst = index === 0
  const isLast = index === NEWS_SLIDES.length - 1
  const isGifSlide = String(slide.image || '').toLowerCase().endsWith('.gif')
  const isCurrentImageLoaded = !slide.image || Boolean(loadedImages[slide.image])

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        setIndex(0)
        if (typeof Sentry.metrics?.count === 'function') {
          Sentry.metrics.count('user_click_news_close', 1)
        }
        const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')
        if (API_URL) {
          fetchWithAuth(
            `${API_URL}/v1/user/show-news`,
            {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ show_news: false }),
            },
            undefined,
            { skipUnauthorizedRedirect: true }
          ).catch(() => {})
        }
        // Deferred so cleanup runs after Radix finishes unmount/DismissableLayer.
        window.setTimeout(restoreBodyPointerEvents, 0)
      }
      onOpenChange(next)
    },
    [onOpenChange]
  )

  React.useEffect(() => {
    if (open) return
    const id = window.setTimeout(restoreBodyPointerEvents, 0)
    return () => window.clearTimeout(id)
  }, [open])

  const goBack = () => {
    if (isFirst) return
    setIndex((i) => i - 1)
  }

  const goNext = () => {
    if (isLast) {
      handleOpenChange(false)
      return
    }
    setIndex((i) => i + 1)
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      {open ? (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/5 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-full max-w-[67.2rem] min-h-[624px] translate-x-[-50%] translate-y-[-50%]',
            'rounded-2xl border bg-background shadow-2xl p-0 overflow-hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'duration-200'
          )}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            restoreBodyPointerEvents()
          }}
        >
          <DialogPrimitive.Title className="sr-only">What&apos;s new</DialogPrimitive.Title>

          <div className="grid grid-cols-1 md:grid-cols-[35%_65%] min-h-[624px]">
          {/* Left: text content */}
          <div className="relative flex flex-col p-8 md:p-10">
            <DialogPrimitive.Close
              className="absolute right-0 top-0 z-10 md:hidden rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset focus:ring-offset-0"
              aria-label="Close"
            >
              <Cross2Icon className="h-4 w-4" />
            </DialogPrimitive.Close>
            <span className="inline-flex w-fit shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-pink-500/15 text-pink-600 dark:text-pink-400 mb-3">
              {slide.tag}
            </span>
            <div className="flex flex-col flex-1 min-w-0 pt-12 md:pt-16">
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-3">
                {slide.title}
              </h2>
              {slide.keywordBadges && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {slide.keywordBadges.map(({ label, icon: Icon }) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1.5 rounded-md border border-input bg-muted/50 px-2.5 py-1 text-xs font-medium text-foreground"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      {label}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-base text-muted-foreground leading-relaxed">
                {slide.description}
              </p>
              {slide.keyFeatures?.length ? (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {slide.keyFeatures.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              ) : null}
            </div>

            {/* Bottom: dots + Next / Close */}
            <div className="flex items-center justify-between mt-8 pt-5">
              <div className="flex items-center gap-1.5">
                {NEWS_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={cn(
                      'h-2 w-2 rounded-full transition-colors',
                      i === index
                        ? 'bg-foreground'
                        : 'bg-muted-foreground/40 hover:bg-muted-foreground/60'
                    )}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={goBack}
                  disabled={isFirst}
                >
                  Back
                </Button>
                <Button
                  variant={isLast ? 'secondary' : 'default'}
                  size="sm"
                  className="rounded-lg"
                  onClick={goNext}
                >
                  {isLast ? 'Close' : 'Next'}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: image or placeholder */}
          <div className="relative hidden md:block bg-muted/30 border-t md:border-t-0 md:border-l overflow-hidden flex-1 min-h-0">
            <div className="absolute inset-0">
              {slide.image ? (
                <>
                  {!isCurrentImageLoaded ? (
                    <div className="absolute inset-0 animate-pulse bg-muted/60" />
                  ) : null}
                  <img
                    src={slide.image}
                    alt=""
                    loading={isGifSlide ? 'lazy' : 'eager'}
                    decoding="async"
                    onLoad={() => {
                      setLoadedImages((prev) => {
                        if (prev[slide.image]) return prev
                        return {
                          ...prev,
                          [slide.image]: true,
                        }
                      })
                    }}
                    onError={() =>
                      setLoadedImages((prev) => ({
                        ...prev,
                        [slide.image]: true,
                      }))
                    }
                    className={cn(
                      'w-full h-full object-cover object-center transition-opacity duration-300',
                      isCurrentImageLoaded ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  Image placeholder
                </div>
              )}
            </div>
            <div className="absolute right-3 top-3 z-10">
              <DialogPrimitive.Close
                className="h-8 w-8 flex items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
                aria-label="Close"
              >
                <Cross2Icon className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
          </div>
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
      ) : null}
    </DialogPrimitive.Root>
  )
}
